import type { BadgeTone } from '@/components/admin/entretiens/status-badge';
import { ApiError } from '@/lib/api';
import { TRUNCATED_OUTPUT_SENTENCE, extractOrigins } from '@/lib/conversation-model';
import { objectiveStatusLabel, objectiveStatusTone, objectiveTone } from '@/lib/session-labels';
import type {
  RangementDiscard,
  RangementDuplicateExtract,
  RangementObjective,
  RangementObjectiveState,
  RangementState,
  RangementTrialRefusalCode,
  RangementTrialSummary,
  RangerCall,
  SessionDetail,
} from '@matter_extractor/shared';
import {
  objectiveState,
  rangementTargetsKept,
  rangementTrialErrorSchema,
  readRangerAnswer,
  unfenced,
} from '@matter_extractor/shared';

/**
 * What Sessions SAYS of the rangement (scenario-succes.md §7, issue #177) -
 * pure, in one module beside the screens that draw it, the régime of
 * `session-noyau.ts` which it follows word for word where the two blocks agree.
 *
 * ## Nothing here ranks and nothing here counts
 *
 * The extracts, the states, the formulas and the discarded passages are the
 * ranger's, read after the server checked every extract against the person's
 * own words (`verifiedRangement`). This module arranges them beside what the
 * scribe held at the end of the conversation, and words them: it files nothing.
 */

/* ------------------------------------------------------------ the block */

/**
 * The states of the block: the four of a rangement, plus `none` - the session
 * has NO rangement, which is a state of the screen and not of the row (§7.2,
 * « aucun »).
 */
export type RangementBlockState = 'none' | RangementState;

/** The badge of the block, in the words of §7.1. */
export const RANGEMENT_STATE_LABEL: Readonly<Record<RangementBlockState, string>> = {
  none: 'Aucun',
  pending: 'En cours',
  rendered: 'Rendu',
  failed: 'En échec',
  'no-matter': 'Sans matière',
};

/**
 * Its tone, on the register of the maquette `RangementEtats`: green for a
 * rendering, red for a failure - the determination read the scribe, and the
 * session is to leave a measure -, blue for one under way, grey otherwise.
 */
export function rangementTone(state: RangementBlockState): BadgeTone {
  if (state === 'rendered') return 'success';
  if (state === 'failed') return 'danger';
  if (state === 'pending') return 'accent';
  return 'neutral';
}

/** The state of an objective in the ranger's column, capitalised for its pill. */
export const RANGEMENT_OBJECTIVE_STATE_LABEL: Readonly<Record<RangementObjectiveState, string>> = {
  portée: 'Portée',
  imprécise: 'Imprécise',
  vide: 'Vide',
};

/** Its tone: green when the words carry it, yellow when they brush it, grey when nothing does. */
export function rangementObjectiveTone(state: RangementObjectiveState): BadgeTone {
  if (state === 'portée') return 'success';
  if (state === 'imprécise') return 'warning';
  return 'neutral';
}

/**
 * The mark before the name of an objective in the pills of a folded rangement (#224, maquette
 * `Main`): a check for the words that carry it, an exclamation mark for the ones that brush it, a
 * plain hyphen for nothing - never colour alone.
 */
export const RANGEMENT_OBJECTIVE_MARK: Readonly<Record<RangementObjectiveState, string>> = {
  portée: '✓',
  imprécise: '!',
  vide: '-',
};

/** The label a target the played version no longer carries is drawn under. */
export const RANGEMENT_GHOST_LABEL = 'Objectif absent de la version jouée';

/** The first sentence under the header: what the block IS, per state. */
export function rangementSentence(
  state: RangementBlockState,
  context: { readonly protocol: string | null; readonly targets: number; readonly trial: boolean },
): string | null {
  if (context.trial) {
    switch (state) {
      case 'pending':
        return 'Le rangeur relit la conversation de cette session, avec la consigne de développement : le rendu arrive dans quelques minutes.';
      case 'failed':
        return 'Le rangeur n’a pas rendu de rangement lisible, relance comprise. Ni le rangement de la session ni sa détermination n’ont bougé.';
      default:
        return 'La colonne du rangeur est celle de cet essai. Ni le rangement de la session ni sa détermination n’ont bougé.';
    }
  }
  switch (state) {
    case 'none':
      return context.targets === 0
        ? `Le protocole joué${context.protocol === null ? '' : `, ${context.protocol},`} n’avait pas d’objectif cible : la détermination a lu la matière du scribe. La colonne du rangeur se remplit d’un rangement à blanc.`
        : 'Aucun rangement n’a été calculé pour cette session (fermée par un garde-fou, expirée, ou jouée avant le rangeur) : la détermination a lu la matière du scribe. La colonne du rangeur se remplit d’un rangement à blanc.';
    case 'pending':
      return 'Le rangeur relit toute la conversation, en fond : la détermination du noyau attend son règlement.';
    case 'no-matter':
      return 'La personne n’a répondu à aucun tour : aucun appel n’a été passé, et il n’y avait rien à ranger.';
    case 'failed':
      return null;
    default:
      return `Le rangeur a relu toute la conversation, une fois, à la fin de l’entretien. La détermination du noyau a lu sa colonne sur ${context.targets <= 1 ? 'cet objectif' : `ces ${context.targets} objectifs`} ; la colonne du scribe est ce qui a piloté l’interviewer pendant l’entretien.`;
  }
}

/** The box of a failure (§7.2): the determination read the scribe, and the session leaves a measure. */
export const RANGEMENT_FAILED_NOTE =
  'La détermination a lu la matière du scribe. Cette session est à écarter d’une mesure du rangeur, sauf à la reranger à blanc.';

/** The line under it, in the maquette: what the ranger's column then says. */
export const RANGEMENT_FAILED_COLUMN_NOTE =
  'La colonne du rangeur affiche « - » sur chaque objectif ; la colonne du scribe est inchangée.';

/* -------------------------------------------------------- the figure */

/** What a rangement and a trial share, as far as this module reads them. */
export interface RangementFigureFacts {
  readonly calls: readonly RangerCall[];
  readonly startedAt: string;
  readonly settledAt: string | null;
  readonly promptFingerprint: string | null;
}

/** The model the API answered on the LAST call that got that far. */
export function rangementModel(figure: Pick<RangementFigureFacts, 'calls'>): string | null {
  for (const call of [...figure.calls].reverse()) {
    if (call.model !== null) return call.model;
  }
  return null;
}

/** From the launch to the settlement; null while it runs and on a stamp that does not parse. */
export function rangementDurationMs(
  figure: Pick<RangementFigureFacts, 'startedAt' | 'settledAt'>,
): number | null {
  if (figure.settledAt === null) return null;
  const started = Date.parse(figure.startedAt);
  const settled = Date.parse(figure.settledAt);
  if (Number.isNaN(started) || Number.isNaN(settled)) return null;
  return Math.max(0, settled - started);
}

/**
 * The fingerprint of the system served: the one the figure records, or the last
 * call's - a figure written before the column existed keeps its calls'.
 */
export function rangementFingerprint(
  figure: Pick<RangementFigureFacts, 'calls' | 'promptFingerprint'>,
): string | null {
  return figure.promptFingerprint ?? figure.calls.at(-1)?.promptFingerprint ?? null;
}

/**
 * Why one attempt of the ranger did not give a rangement (§7.2: « les essais et
 * leur cause »), or null on the attempt that did.
 *
 * Read the way `attemptCause` reads the analyst's: a call that never came back
 * says what the provider said; one that came back UNUSABLE is read again here,
 * the cut answer first - its JSON ends mid-way, and « pas du JSON » would send
 * a reader to the prompt when the repair is the ceiling (#96).
 */
export function rangementAttemptCause(call: RangerCall): string | null {
  if (call.failedReason !== null) return `L’appel n’a pas abouti : ${call.failedReason}`;
  if (!call.malformed) return null;
  if (call.stopReason === 'max_tokens') return TRUNCATED_OUTPUT_SENTENCE;
  try {
    JSON.parse(unfenced(call.output));
  } catch {
    return 'Sortie illisible : la réponse n’est pas du JSON.';
  }
  return readRangerAnswer(call.output) === null
    ? 'Sortie de mauvaise forme : le contrat du rangeur n’est pas tenu.'
    : 'Sortie écartée à l’époque, lisible aujourd’hui.';
}

/** « 5 portées, 1 imprécise, 3 écartés » - what a rendering came to, zeros left out. */
export function rangementSummary(
  objectives: readonly Pick<RangementObjective, 'state'>[],
  discarded: readonly RangementDiscard[],
): string {
  const count = (state: RangementObjectiveState) =>
    objectives.filter((objective) => objective.state === state).length;
  const parts: string[] = [];
  const carried = count('portée');
  const vague = count('imprécise');
  const empty = count('vide');
  if (carried > 0) parts.push(`${carried} ${carried > 1 ? 'portées' : 'portée'}`);
  if (vague > 0) parts.push(`${vague} ${vague > 1 ? 'imprécises' : 'imprécise'}`);
  if (empty > 0) parts.push(`${empty} ${empty > 1 ? 'vides' : 'vide'}`);
  if (discarded.length > 0) {
    parts.push(`${discarded.length} ${discarded.length > 1 ? 'écartés' : 'écarté'}`);
  }
  return parts.length === 0 ? 'rien de rangé' : parts.join(', ');
}

/** « 2 extraits retirés » - what the verbatim check removed (§3.2), or null when it removed none. */
export function removedExtractsLabel(count: number): string | null {
  if (count <= 0) return null;
  return `${count} ${count > 1 ? 'extraits retirés' : 'extrait retiré'}`;
}

/**
 * What the one-objective-per-passage setting removed (relecture.md §12, issue
 * #187): « 1 passage rangé deux fois : gardé en 1. Tension vitale, retiré de 6.
 * Sens profond ». Null when it removed nothing - the setting unticked, and every
 * rangement written before it existed.
 *
 * An objective is named by its place among the rows of the table (`ids`, the
 * order of the protocol) and its name, the way the review numbers its cases;
 * one the table cannot place keeps its bare name. Several passages are counted
 * first, then told one after the other, separated by « ; ».
 */
export function duplicateExtractsLabel(
  duplicates: readonly Pick<RangementDuplicateExtract, 'objectiveId' | 'removedFrom'>[],
  ids: readonly string[],
  nameOf: (objectiveId: string) => string,
): string | null {
  if (duplicates.length === 0) return null;
  const named = (objectiveId: string): string => {
    const at = ids.indexOf(objectiveId);
    return at < 0 ? nameOf(objectiveId) : `${at + 1}. ${nameOf(objectiveId)}`;
  };
  const told = duplicates.map(
    (duplicate) =>
      `gardé en ${named(duplicate.objectiveId)}, retiré de ${named(duplicate.removedFrom)}`,
  );
  const count =
    duplicates.length > 1
      ? `${duplicates.length} passages rangés deux fois`
      : '1 passage rangé deux fois';
  return `${count} : ${told.join(' ; ')}`;
}

/* ------------------------------------------------------------- the rows */

/** One target of the rangement, named by the protocol the session played. */
export interface RangementTarget {
  readonly objectiveId: string;
  readonly name: string;
  /** 1-based rank of the objective in its step. */
  readonly rank: number | null;
  readonly stepName: string | null;
  /** True for a target the played version does not carry. */
  readonly ghost: boolean;
}

/** The targets the PLAYED protocol still carries, in the order of the protocol (§4). */
export function playedTargetIds(detail: SessionDetail): string[] {
  const protocol = detail.cascade.protocol;
  if (protocol === null) return [];
  return [
    ...rangementTargetsKept(protocol.content.steps, protocol.content.rangement).targetObjectiveIds,
  ];
}

/**
 * Does the parcours draw the block « Rangement de la matière »? When the session HAS a
 * rangement, or the protocol it played carries objective targets - a missing rangement then
 * reads « aucun ». One place for the question: the walk, the sommaire (`lib/session-toc.ts`) and
 * the route's section for the dry trials of a session WITHOUT the block all ask it.
 */
export function rangementBlockDrawn(detail: SessionDetail): boolean {
  return detail.rangement !== null || playedTargetIds(detail).length > 0;
}

/** One objective, named by the played protocol - or the ghost label when it does not carry it. */
export function rangementTargetOf(detail: SessionDetail, objectiveId: string): RangementTarget {
  const protocol = detail.cascade.protocol;
  for (const step of protocol?.content.steps ?? []) {
    const at = step.objectives.findIndex((objective) => objective.id === objectiveId);
    const objective = step.objectives[at];
    if (objective !== undefined) {
      return {
        objectiveId,
        name: objective.name,
        rank: at + 1,
        stepName: step.name,
        ghost: false,
      };
    }
  }
  return { objectiveId, name: RANGEMENT_GHOST_LABEL, rank: null, stepName: null, ghost: true };
}

/** One extract with the turn that carries it, as the two columns print it. */
export interface RowExtract {
  /** 1-based, the number Sessions prints. */
  readonly turn: number;
  readonly text: string;
}

/** What the scribe held for one objective at the END of the conversation. */
export interface ScribeReading {
  readonly label: string;
  readonly tone: BadgeTone;
  /** Its LIVING extracts, each with the turn that brought it. */
  readonly extracts: readonly RowExtract[];
}

/**
 * The scribe's column: the status and the living extracts of the last snapshot
 * (§7.1), the turn of each read the way the Matière tab reads it
 * (`extractOrigins`, by position).
 */
export function scribeReading(detail: SessionDetail, objectiveId: string): ScribeReading {
  const turns = detail.turns;
  const last = turns.at(-1);
  const state =
    last === undefined
      ? ({ status: 'empty' } as const)
      : objectiveState(last.snapshot, objectiveId);
  const tone = objectiveTone(state);
  const extracts = extractOrigins(turns, objectiveId, turns.length)
    .filter((extract) => !extract.invalidated)
    .map((extract) => ({ turn: extract.firstTurn, text: extract.text }));
  return {
    label: objectiveStatusLabel(tone),
    tone: objectiveStatusTone(tone),
    extracts,
  };
}

/**
 * « t9 · Déploiement » - a turn and the step it was played in, the number
 * Sessions prints being the turn's `index` plus one. A turn the session does
 * not hold, or a step the frozen protocol no longer carries, keeps what is
 * known: « t9 » alone.
 */
export function turnPlace(detail: SessionDetail, turnNumber: number): string {
  const turn = detail.turns.find((one) => one.index + 1 === turnNumber);
  if (turn === undefined) return `t${turnNumber}`;
  const step = detail.cascade.protocol?.content.steps.find((one) => one.id === turn.stepId);
  const name = step?.name.trim() ?? '';
  return name === '' ? `t${turnNumber}` : `t${turnNumber} · ${name}`;
}

/**
 * The objectives the table draws, in order: the targets the played protocol
 * carries; and, once the figure is known, the objectives it rendered that the
 * protocol does not carry, after them - a rendering is never dropped on a row
 * that cannot be named.
 */
export function rangementRowIds(
  detail: SessionDetail,
  objectives: readonly Pick<RangementObjective, 'objectiveId'>[] | null,
): string[] {
  const ids = playedTargetIds(detail);
  for (const objective of objectives ?? []) {
    if (!ids.includes(objective.objectiveId)) ids.push(objective.objectiveId);
  }
  return ids;
}

/* ------------------------------------------------------------- the trial */

/** What the list of a session's trials says of one: what came out, or its state. */
export function rangementTrialOutcome(
  trial: Pick<RangementTrialSummary, 'state' | 'objectives' | 'discarded'>,
): string {
  if (trial.state === 'pending') return 'en cours';
  if (trial.state === 'failed') return 'en échec';
  return rangementSummary(trial.objectives ?? [], trial.discarded ?? []);
}

/** Why a trial was refused, in the operator's words - worded from the `code`, never the message. */
export const RANGEMENT_TRIAL_REFUSAL_SENTENCE: Readonly<Record<RangementTrialRefusalCode, string>> =
  {
    'no-protocol':
      'Cette session n’a joué aucun protocole lisible : le rangeur n’a pas de consigne à appliquer.',
    'no-matter': 'La personne n’a répondu à aucun tour : le rangeur n’aurait rien à ranger.',
    'protocol-retired':
      'Le protocole de cette session est retiré : sa version de développement ne se lit plus.',
    'rangement-missing':
      'La version de développement du protocole n’a pas d’objectif cible pour le rangeur.',
  };

/** The code of a trial's refusal, or null on any other failure - a 409 whose body is the contract. */
export function rangementTrialRefusalOf(error: unknown): RangementTrialRefusalCode | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const refusal = rangementTrialErrorSchema.safeParse(error.details);
  return refusal.success ? refusal.data.code : null;
}

/**
 * The search a link to the Rangement view of a protocol carries (`?vue=rangement`,
 * §4) - with the version when the link is to a version's own consigne.
 */
export function rangementEditorSearch(versionId: string | null): {
  version?: string;
  vue?: 'rangement';
} {
  const vue = 'rangement';
  return versionId === null ? { vue } : { version: versionId, vue };
}
